import {Component, effect, ElementRef, input, output, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {Observable, timer} from 'rxjs';
import {IconComponent} from '../icon/icon.component';
import {NgTemplateOutlet} from '@angular/common';

export interface EditableSaveResult {
  success: boolean | Observable<any>;
}

export interface EditableEditingEvent {
  focus?: HTMLElement | ElementRef<HTMLElement>
}

@Component({
  selector: 'app-editable',
  imports: [
    ReactiveFormsModule,
    IconComponent,
    NgTemplateOutlet
  ],
  templateUrl: './editable.component.html',
  host: {
    class: 'app-row app-fill-first centered-items gapped tight'
  }
})
export class EditableComponent {
  readonly editMode = input<'always' | 'manual' | 'never'>('manual')
  readonly align = input<string, 'center' | 'top'>(
    'center',
    {transform: (val: 'center' | 'top') => val === 'top' ? 'align-start' : 'centered'}
  )
  readonly valid = input<boolean, any>(
    true,
    {transform: (val: any) => !!val}
  )
  readonly saving = output<EditableSaveResult>()
  readonly saved = output()
  readonly canceled = output()
  readonly editing = output<EditableEditingEvent>()
  readonly isEditing = signal(false)
  readonly isSaving = signal(false)

  constructor() {
    effect(() => {
      if (this.editMode() === 'always') {
        this.isEditing.set(true)
      }
    })

    effect(() => {
      if (this.isEditing()) {
        const event: EditableEditingEvent = {}
        this.editing.emit(event)
        if (event.focus) {
          this.focus(event.focus)
        }
      }
    });
  }

  protected get always() {
    return this.editMode() === 'always'
  }

  protected get manual() {
    return this.editMode() === 'manual'
  }

  save() {
    if (this.manual) {
      const result: EditableSaveResult = {
        success: true
      }

      this.isSaving.set(true)
      this.saving.emit(result)

      if (result.success instanceof Observable) {
        result.success.subscribe({
          next: () => this.commit()
        })
      } else if (result.success) {
        this.commit()
      }
    }
  }

  protected commit() {
    this.isEditing.set(false)
    this.isSaving.set(false)
    this.saved.emit()
  }

  cancel() {
    if (this.manual) {
      this.isEditing.set(false)
      this.canceled.emit()
    }
  }

  focus(target: HTMLElement | ElementRef<HTMLElement>) {
    timer(100).subscribe(() => {
      if (target instanceof ElementRef) {
        target = target.nativeElement
      }
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
      target.focus()
    })
  }
}
